import React, { useEffect, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";

const LIMIT = 10;

export const AdminFuel = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [stats, setStats] = useState({ todayAmount: 0, weekAmount: 0, monthAmount: 0, avgRate: 0, totalRecords: 0, litersByType: {} });
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterPayMethod, setFilterPayMethod] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { vehicles, payMethod } = useFuelDropdowns();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const openEdit = (item) => setEditItem(item);
  const closeEdit = () => setEditItem(null);

  const buildParams = (pageVal, vehicleId, from, to, payMethod) => {
    const params = { page: pageVal, limit: LIMIT };
    if (vehicleId) params.vehicleId = vehicleId;
    if (payMethod) params.paymentMethod = payMethod;
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;
    return params;
  };

  const getData = async (pageVal = page, vehicleId = filterVehicle, from = dateFrom, to = dateTo, pm = filterPayMethod) => {
    try {
      setLoader(true);
      const response = await api.getFuel(buildParams(pageVal, vehicleId, from, to, pm));
      if (response?.success) {
        setData(response?.records || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  const getStats = async (vehicleId = filterVehicle, from = dateFrom, to = dateTo, pm = filterPayMethod) => {
    try {
      const params = { page: 1, limit: 1000 };
      if (vehicleId) params.vehicleId = vehicleId;
      if (pm) params.paymentMethod = pm;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
      const response = await api.getFuel(params);
      if (response?.success) {
        const records = response?.records || [];
        const now = moment();
        const n = (v) => parseFloat(v) || 0;
        const todayAmount = records.filter(r => moment(r.date).isSame(now, "day")).reduce((sum, r) => sum + n(r.amount), 0);
        const weekAmount = records.filter(r => moment(r.date).isAfter(moment().subtract(7, "days"))).reduce((sum, r) => sum + n(r.amount), 0);
        const monthAmount = records.filter(r => moment(r.date).isSame(now, "month")).reduce((sum, r) => sum + n(r.amount), 0);
        const avgRate = records.length > 0 ? Math.round(records.reduce((sum, r) => sum + n(r.rate), 0) / records.length) : 0;
        const monthRecords = records.filter(r => moment(r.date).isSame(now, "month"));
        const litersByType = {};
        monthRecords.forEach(r => {
          const ft = r?.vehicleId?.fuelType || "other";
          litersByType[ft] = (litersByType[ft] || 0) + n(r.liters);
        });
        setStats({ todayAmount, weekAmount, monthAmount, avgRate, totalRecords: records.length, litersByType });
      }
    } catch (e) {}
  };

  useEffect(() => { getData(1); getStats(); }, []);

  const applyFilters = (vehicleId, from, to, pm) => {
    setPage(1);
    getData(1, vehicleId, from, to, pm);
    getStats(vehicleId, from, to, pm);
  };

  const handleVehicleFilter = (vehicleId) => {
    setFilterVehicle(vehicleId);
    applyFilters(vehicleId, dateFrom, dateTo, filterPayMethod);
  };

  const handlePayMethodFilter = (pm) => {
    setFilterPayMethod(pm);
    applyFilters(filterVehicle, dateFrom, dateTo, pm);
  };

  const handleDateFrom = (val) => {
    setDateFrom(val);
    applyFilters(filterVehicle, val, dateTo, filterPayMethod);
  };

  const handleDateTo = (val) => {
    setDateTo(val);
    applyFilters(filterVehicle, dateFrom, val, filterPayMethod);
  };

  const clearFilters = () => {
    setFilterVehicle("");
    setFilterPayMethod("");
    setDateFrom("");
    setDateTo("");
    applyFilters("", "", "", "");
  };

  const handlePageChange = (e, value) => { setPage(value); getData(value); };

  const handlePrint = async () => {
    try {
      const params = { page: 1, limit: 1000 };
      if (filterVehicle) params.vehicleId = filterVehicle;
      if (filterPayMethod) params.paymentMethod = filterPayMethod;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const response = await api.getFuel(params);
      const records = response?.records || [];
      const selVehicle = vehicles.find(v => v._id === filterVehicle);
      const selPayMethod = payMethod.find(p => p._id === filterPayMethod);
      const filterLabel = [
        selVehicle ? `Vehicle: ${selVehicle.numberPlate?.toUpperCase()}` : "",
        selPayMethod ? `Pay Through: ${selPayMethod.name}` : "",
        dateFrom ? `From: ${moment(dateFrom).format("MMM Do YYYY")}` : "",
        dateTo ? `To: ${moment(dateTo).format("MMM Do YYYY")}` : "",
      ].filter(Boolean).join("  |  ");
      const totalAmount = records.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      const totalLiters = records.reduce((s, r) => s + (parseFloat(r.liters) || 0), 0);
      const win = window.open("", "_blank");
      win.document.write(`<!DOCTYPE html><html><head><title>Fuel Report — Shabbir Transport</title>
        <style>
          body{font-family:Arial,sans-serif;padding:28px;color:#1a1a1a;font-size:13px}
          h2{margin:0 0 4px;font-size:20px}
          .meta{color:#666;font-size:12px;margin-bottom:6px}
          .filter-label{color:#4338ca;font-size:12px;font-weight:600;margin-bottom:16px}
          table{width:100%;border-collapse:collapse;margin-top:8px}
          th{background:#f1f5f9;font-weight:700;padding:8px 10px;border:1px solid #ddd;text-align:left;font-size:12px}
          td{padding:7px 10px;border:1px solid #e5e7eb;font-size:12px}
          tr:nth-child(even) td{background:#f8fafc}
          .amount{font-weight:700;color:#065f46}
          tfoot td{font-weight:700;background:#f1f5f9;font-size:13px}
          @media print{body{padding:16px}}
        </style>
      </head><body>
        <h2>Shabbir Transport — Fuel Report</h2>
        <div class="meta">Generated: ${moment().format("MMM Do YYYY, h:mm a")} &nbsp;·&nbsp; Total Records: ${records.length}</div>
        ${filterLabel ? `<div class="filter-label">Filters: ${filterLabel}</div>` : ""}
        <table>
          <thead><tr>
            <th>#</th><th>Date</th><th>Number Plate</th><th>Vehicle Type</th>
            <th>Fuel Type</th><th>Pay Through</th><th>Rate</th><th>Liters</th><th>Amount</th>
          </tr></thead>
          <tbody>
            ${records.map((r, i) => `<tr>
              <td>${i + 1}</td>
              <td>${moment(r.date).format("MMM Do YYYY")}</td>
              <td><b>${r.vehicleId?.numberPlate?.toUpperCase() || "—"}</b></td>
              <td style="text-transform:capitalize">${r.vehicleId?.type || "—"}</td>
              <td style="text-transform:capitalize">${r.vehicleId?.fuelType || "—"}</td>
              <td>${r.paymentMethod?.name || "—"}</td>
              <td>Rs. ${r.rate}</td>
              <td>${r.liters} L</td>
              <td class="amount">Rs. ${r.amount}</td>
            </tr>`).join("")}
          </tbody>
          <tfoot><tr>
            <td colspan="7"></td>
            <td>Total: ${totalLiters.toFixed(1)} L</td>
            <td>Total: Rs. ${totalAmount.toLocaleString()}</td>
          </tr></tfoot>
        </table>
      </body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    } catch (e) {
      notify.error("Could not generate report");
    }
  };

  const deleteFuel = async (id) => {
    if (!window.confirm("Are you sure you want to delete this fuel record?")) return;
    try {
      const response = await api.deleteFuel(id);
      if (response?.success) {
        notify.success("Record deleted successfully!");
        getData(page);
        getStats();
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  const hasFilters = filterVehicle || filterPayMethod || dateFrom || dateTo;
  const selectedVehicle = vehicles.find(v => v._id === filterVehicle);
  const selectedPayMethod = payMethod.find(p => p._id === filterPayMethod);

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Fuel ({total})</h3>
              <button onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: 6, background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                <i className="fa-solid fa-file-arrow-down" /> Download / Print
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-car" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" }} />
                <select value={filterVehicle} onChange={e => handleVehicleFilter(e.target.value)}
                  style={{ paddingLeft: 28, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", outline: "none", minWidth: 150, cursor: "pointer" }}>
                  <option value="">All Vehicles</option>
                  {vehicles.map(v => <option key={v._id} value={v._id}>{v.numberPlate?.toUpperCase()}</option>)}
                </select>
              </div>
              <div style={{ position: "relative" }}>
                <i className="fa-solid fa-credit-card" style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" }} />
                <select value={filterPayMethod} onChange={e => handlePayMethodFilter(e.target.value)}
                  style={{ paddingLeft: 28, paddingRight: 10, paddingTop: 7, paddingBottom: 7, border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", outline: "none", minWidth: 150, cursor: "pointer" }}>
                  <option value="">All Pay Through</option>
                  {payMethod.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>From</span>
                <input type="date" value={dateFrom} onChange={e => handleDateFrom(e.target.value)}
                  style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>To</span>
                <input type="date" value={dateTo} onChange={e => handleDateTo(e.target.value)}
                  style={{ border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }} />
              </div>
              {hasFilters && (
                <button onClick={clearFilters}
                  style={{ background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 7, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />Clear
                </button>
              )}
            </div>
            {hasFilters && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {selectedVehicle && <span style={{ fontSize: 11, background: "#ede9fe", color: "#6d28d9", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}><i className="fa-solid fa-car" style={{ marginRight: 4 }} />{selectedVehicle.numberPlate?.toUpperCase()}</span>}
                {selectedPayMethod && <span style={{ fontSize: 11, background: "#f0fdf4", color: "#15803d", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}><i className="fa-solid fa-credit-card" style={{ marginRight: 4 }} />{selectedPayMethod.name}</span>}
                {dateFrom && <span style={{ fontSize: 11, background: "#dbeafe", color: "#1d4ed8", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>From: {moment(dateFrom).format("MMM Do YYYY")}</span>}
                {dateTo && <span style={{ fontSize: 11, background: "#dbeafe", color: "#1d4ed8", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>To: {moment(dateTo).format("MMM Do YYYY")}</span>}
              </div>
            )}
            <FuelStats stats={stats} />
            {loader ? (
              <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
            ) : (
              <Table data={data} deleteFuel={deleteFuel} openEdit={openEdit} />
            )}
            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#10b981", color: "white", "&:hover": { backgroundColor: "#059669" } },
                      "& .MuiPaginationItem-root": { "&:hover": { backgroundColor: "#d1fae5", color: "#059669" } },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
        <div className="AddEmpBtn" onClick={handleShow}><i className="fa-solid fa-plus"></i></div>
      </div>
      <CreateFuelModal show={show} handleClose={handleClose} getFuel={() => { getData(page); getStats(); }} />
      {editItem && <EditFuelModal item={editItem} handleClose={closeEdit} getFuel={() => { getData(page); getStats(); }} />}
    </>
  );
};

const fuelTypeColors = {
  petrol: { bg: "#fffbeb", color: "#b45309", iconBg: "#fef3c7" },
  diesel: { bg: "#eff6ff", color: "#1d4ed8", iconBg: "#dbeafe" },
  cng: { bg: "#f0fdf4", color: "#15803d", iconBg: "#dcfce7" },
  other: { bg: "#f8fafc", color: "#475569", iconBg: "#e2e8f0" },
};

const FuelStats = ({ stats }) => {
  const cards = [
    { label: "Today's Spend", value: `Rs. ${stats.todayAmount.toLocaleString()}`, icon: "fa-calendar-day", bg: "#eff6ff", color: "#1d4ed8", iconBg: "#dbeafe" },
    { label: "Last 7 Days", value: `Rs. ${stats.weekAmount.toLocaleString()}`, icon: "fa-calendar-week", bg: "#f5f3ff", color: "#7c3aed", iconBg: "#ede9fe" },
    { label: "This Month", value: `Rs. ${stats.monthAmount.toLocaleString()}`, icon: "fa-calendar", bg: "#fff7ed", color: "#c2410c", iconBg: "#ffedd5" },
    { label: "Avg. Rate / Liter", value: `Rs. ${stats.avgRate}`, icon: "fa-chart-line", bg: "#fdf4ff", color: "#9333ea", iconBg: "#f3e8ff" },
    { label: "Total Records", value: stats.totalRecords, icon: "fa-list", bg: "#f8fafc", color: "#475569", iconBg: "#e2e8f0" },
  ];
  const literEntries = Object.entries(stats.litersByType || {});
  return (
    <div style={{ marginBottom: 24, marginTop: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
        {cards.map((card, i) => (
          <div key={i} style={{ background: card.bg, borderRadius: 10, padding: "14px 16px", border: `1px solid ${card.iconBg}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: card.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fa-solid ${card.icon}`} style={{ color: card.color, fontSize: 14 }} />
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: card.color, marginBottom: 2 }}>{card.value}</div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{card.label}</div>
          </div>
        ))}
      </div>
      {literEntries.length > 0 && (
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: "14px 18px", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            <i className="fa-solid fa-gas-pump" style={{ marginRight: 6 }} />Liters Consumed This Month — by Fuel Type
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {literEntries.map(([type, liters]) => {
              const c = fuelTypeColors[type] || fuelTypeColors.other;
              return (
                <div key={type} style={{ background: c.bg, border: `1px solid ${c.iconBg}`, borderRadius: 8, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{parseFloat(liters.toFixed(2))} L</span>
                  <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500, textTransform: "capitalize" }}>{type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const NumberPlate = ({ number }) => (
  <div style={{
    display: "inline-block",
    background: "#fff",
    border: "2.5px solid #222",
    borderRadius: 6,
    overflow: "hidden",
    minWidth: 120,
    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
  }}>
    <div style={{
      background: "repeating-linear-gradient(90deg, #c0392b 0px, #c0392b 10px, #e74c3c 10px, #e74c3c 14px, #c0392b 14px, #c0392b 24px)",
      height: 8,
      width: "100%",
    }} />
    <div style={{
      padding: "4px 10px 2px",
      textAlign: "center",
      fontWeight: 900,
      fontSize: 15,
      letterSpacing: 3,
      color: "#111",
      fontFamily: "'Courier New', monospace",
      textTransform: "uppercase",
    }}>
      {number || "—"}
    </div>
    <div style={{
      textAlign: "center",
      fontSize: 8,
      fontWeight: 700,
      letterSpacing: 4,
      color: "#444",
      paddingBottom: 4,
      textTransform: "uppercase",
    }}>
      SINDH
    </div>
  </div>
);

const Table = ({ data, deleteFuel, openEdit }) => {
  const formatDate = (date) => moment(date).format("MMM Do YYYY, h:mm a");
  return (
    <div className="TableMain mt-3">
      <div className="table-responsive">
        <table className="table table-hover drivers-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>No. Plate</th>
              <th>Vehicle Type</th>
              <th>Fuel Type</th>
              <th>Pay Through</th>
              <th>Rate</th>
              <th>Liters</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? data.map((item, key) => (
              <tr key={key}>
                <td><div className="table-text-dark w-max-c db">{formatDate(item?.date)}</div></td>
                <td><NumberPlate number={item?.vehicleId?.numberPlate} /></td>
                <td><span className="table-text-dark w-max-c db" style={{textTransform:"capitalize"}}>{item?.vehicleId?.type}</span></td>
                <td><span className="table-text-dark w-max-c db" style={{textTransform:"capitalize"}}>{item?.vehicleId?.fuelType}</span></td>
                <td><span className="table-text-dark w-max-c db">{item?.paymentMethod?.name}</span></td>
                <td><span className="table-text-dark w-max-c db">Rs. {item?.rate}</span></td>
                <td><span className="table-text-dark w-max-c db">{item?.liters} L</span></td>
                <td><span className="table-text-dark w-max-c db" style={{fontWeight:600, color:"#065f46"}}>Rs. {item?.amount}</span></td>
                <td>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="edit p-2 cursor-pointer" onClick={() => openEdit(item)}>
                      <i className="fa-solid fa-pen-to-square"></i>
                    </div>
                    <div className="delete p-2 cursor-pointer" onClick={() => deleteFuel(item?._id)}>
                      <i className="fa-solid fa-trash"></i>
                    </div>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={9} className="text-center py-4 text-muted">No fuel records found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FuelForm = ({ data, onChange, vehicles, payMethod }) => (
  <div className="row">
    <div className="col-12">
      <div className="form-group">
        <label>Date &amp; Time</label>
        <input type="datetime-local" name="date" className="form-control" value={data?.date || ""} onChange={onChange} />
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Rate (per liter)</label>
        <input type="number" name="rate" className="form-control" value={data?.rate || ""} onChange={onChange} />
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Liters</label>
        <input type="number" name="liters" className="form-control" value={data?.liters || ""} onChange={onChange} />
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Amount (auto-calculated)</label>
        <input type="number" name="amount" className="form-control" value={data?.amount || ""} onChange={onChange} style={{ background: "#f8f9fa" }} placeholder="Fill Rate & Liters to auto-calculate" />
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Payment Through</label>
        <select name="paymentMethod" className="form-control" value={data?.paymentMethod || ""} onChange={onChange}>
          <option value="">Select</option>
          {payMethod.map((item, key) => <option value={item?._id} key={key}>{item?.name}</option>)}
        </select>
      </div>
    </div>
    <div className="col-12">
      <div className="form-group">
        <label>Assigned Vehicle</label>
        <select name="vehicleId" className="form-control" value={data?.vehicleId || ""} onChange={onChange}>
          <option value="">Select</option>
          {vehicles.map((item, key) => <option value={item?._id} key={key}>{item?.numberPlate}</option>)}
        </select>
      </div>
    </div>
  </div>
);

const useAutoCalc = (data, setData) => {
  const onChange = (e) => {
    const updated = { ...data, [e.target.name]: e.target.value };
    const rate = parseFloat(e.target.name === "rate" ? e.target.value : updated.rate) || 0;
    const liters = parseFloat(e.target.name === "liters" ? e.target.value : updated.liters) || 0;
    if (rate && liters) updated.amount = (rate * liters).toFixed(2);
    setData(updated);
  };
  return onChange;
};

const useFuelDropdowns = () => {
  const [vehicles, setVehicles] = useState([]);
  const [payMethod, setPayMethod] = useState([]);
  useEffect(() => {
    (async () => {
      const [v, p] = await Promise.all([api.getVehicle(), api.getPayMethod()]);
      if (v?.success) setVehicles(v?.vehicles || []);
      if (p?.success) setPayMethod(p?.methods || []);
    })();
  }, []);
  return { vehicles, payMethod };
};

const CreateFuelModal = ({ show, handleClose, getFuel }) => {
  const [data, setData] = useState({ date: moment().format("YYYY-MM-DDTHH:mm") });
  const onChange = useAutoCalc(data, setData);
  const { vehicles, payMethod } = useFuelDropdowns();

  const handleSubmit = async () => {
    try {
      const payload = { amount: data?.amount, rate: data?.rate, paymentMethod: data?.paymentMethod, liters: data?.liters, vehicleId: data?.vehicleId, date: data?.date ? new Date(data.date).toISOString() : undefined };
      const response = await api.addFuel(payload);
      if (response?.success) {
        getFuel();
        notify.success("Fuel successfully added!");
        setData({});
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Add Fuel Record</div>
        <FuelForm data={data} onChange={onChange} vehicles={vehicles} payMethod={payMethod} />
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Submit</button>
        </div>
      </div>
    </Modal>
  );
};

const EditFuelModal = ({ item, handleClose, getFuel }) => {
  const [data, setData] = useState({
    rate: item?.rate || "",
    liters: item?.liters || "",
    amount: item?.amount || "",
    paymentMethod: item?.paymentMethod?._id || "",
    vehicleId: item?.vehicleId?._id || "",
    date: item?.date ? moment(item.date).format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
  });
  const onChange = useAutoCalc(data, setData);
  const { vehicles, payMethod } = useFuelDropdowns();

  const handleSubmit = async () => {
    try {
      const payload = { ...data, date: data?.date ? new Date(data.date).toISOString() : undefined };
      const response = await api.updateFuel(item._id, payload);
      if (response?.success) {
        getFuel();
        notify.success("Fuel record updated!");
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Modal show={true} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">Edit Fuel Record</div>
        <FuelForm data={data} onChange={onChange} vehicles={vehicles} payMethod={payMethod} />
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Update</button>
        </div>
      </div>
    </Modal>
  );
};
